import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import AdminLeads from './Leads';
import AdminIntel from './AdminIntel';
import AdminPlans from './AdminPlans';
import AdminContent from './AdminContent';
import AdminCaseStudies from './AdminCaseStudies';
import AdminInsights from './AdminInsights';
import AdminSettings from './AdminSettings';
import api from '../../services/api';

const menuItems = [
  { id: 'leads', icon: 'Users', label: 'Leads & Submissions' },
  { id: 'intel', icon: 'Eye', label: 'Client Intelligence Portal' },
  { id: 'content', icon: 'FileText', label: 'Pages & Content' },
  { id: 'plans', icon: 'Tag', label: 'Plans & Pricing' },
  { id: 'case-studies', icon: 'Briefcase', label: 'Case Studies' },
  { id: 'insights', icon: 'BookOpen', label: 'Insights & Articles' },
  { id: 'settings', icon: 'Settings', label: 'Database & API Config' },
];

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('leads');
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('maxmark_admin_token');
    if (token) {
      api.adminVerify()
        .then(res => {
          if (res && res.user) {
            setUser(res.user);
          } else {
            localStorage.removeItem('maxmark_admin_token');
          }
        })
        .catch(() => {
          localStorage.removeItem('maxmark_admin_token');
        })
        .finally(() => {
          setCheckingAuth(false);
        });
    } else {
      setCheckingAuth(false);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError(null);

    try {
      const res = await api.adminLogin({ username, password });
      if (res.token) {
        localStorage.setItem('maxmark_admin_token', res.token);
        setUser(res.user);
      }
    } catch (err) {
      setLoginError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem('maxmark_admin_token');
    setUser(null);
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-zinc-500 text-sm">Verifying security credentials...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4 pt-20">
        <div className="max-w-md w-full bg-zinc-950 p-8 md:p-12 rounded-[2.5rem] border border-zinc-900 shadow-2xl">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-serif font-bold text-white mb-2">Maxmark Admin</h1>
            <p className="text-zinc-500 text-xs">Sign in with your cPanel administrator credentials.</p>
          </div>

          {loginError && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-900/30 text-red-400 text-xs rounded-xl">
              {loginError}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Username or Email</label>
              <input 
                type="text" 
                className="input-field" 
                value={username} 
                onChange={e => setUsername(e.target.value)} 
                placeholder="admin or admin@maxmarkagency.com"
                required 
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Password</label>
              <input 
                type="password" 
                className="input-field" 
                value={password} 
                onChange={e => setPassword(e.target.value)} 
                placeholder="••••••••"
                required 
              />
            </div>

            <button 
              type="submit" 
              disabled={loginLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-bold uppercase tracking-widest text-[11px] transition-all disabled:opacity-50 shadow-lg shadow-blue-600/20"
            >
              {loginLoading ? 'Authenticating...' : 'Sign In to Portal'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex pt-20">
      <aside className="w-64 border-r border-zinc-900 bg-zinc-950 p-6 flex-shrink-0 flex flex-col justify-between">
        <div>
          <div className="mb-10 px-4">
            <h2 className="text-[10px] font-bold text-zinc-600 uppercase tracking-[0.3em] mb-1">Maxmark Agency</h2>
            <p className="text-xs text-white font-medium">{user.full_name || user.username}</p>
            <span className="text-[9px] uppercase tracking-wider text-blue-500 font-semibold">{user.role || 'Admin'}</span>
          </div>
          <nav className="space-y-1">
            {menuItems.map(item => (
              <button 
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-widest transition-all ${activeTab === item.id ? 'bg-blue-600 text-white' : 'text-zinc-500 hover:bg-zinc-900 hover:text-white'}`}
              >
                <SafeIcon name={item.icon} className="w-4 h-4" />
                {item.label}
              </button>
            ))}
          </nav>
        </div>
        <div className="px-4 pt-10 border-t border-zinc-900">
          <button onClick={handleSignOut} className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 hover:text-red-500 transition-colors">Sign Out</button>
        </div>
      </aside>

      <main className="flex-grow p-8 md:p-12 overflow-y-auto">
        <header className="flex justify-between items-center mb-10 pb-6 border-b border-zinc-900">
          <div>
            <h1 className="text-3xl font-serif font-bold capitalize text-white">{activeTab.replace('-', ' ')}</h1>
            <p className="text-xs text-zinc-500 mt-1">Live cPanel MySQL database records & telemetry.</p>
          </div>
        </header>

        {activeTab === 'leads' && <AdminLeads />}
        {activeTab === 'intel' && <AdminIntel />}
        {activeTab === 'content' && <AdminContent />}
        {activeTab === 'plans' && <AdminPlans />}
        {activeTab === 'case-studies' && <AdminCaseStudies />}
        {activeTab === 'insights' && <AdminInsights />}
        {activeTab === 'settings' && <AdminSettings />}
      </main>
    </div>
  );
}